import React, { useState, useEffect } from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import { Heading, Text, Button } from '../styles/components';
import AssessmentForm from '../components/AssessmentForm';

import Header from '../components/Header';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  padding-top: 100px;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  background-color: ${props => props.theme.colors.bone};
  background-image: url('/assets/patterns/Contours - clipped (light).svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;



// New 2-Col Hero Structure
const HeroSection = styled.section`
  background-color: ${props => props.theme.colors.charcoal};
  background-image: url('/assets/patterns/Contours - clipped (light).svg');
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  padding: ${props => props.theme.spacing.xxl} 0;
  
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 800px; /* Taller hero for impact */

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    min-height: auto;
    padding: ${props => props.theme.spacing.xl} 0;
    flex-direction: column;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: ${props => props.theme.spacing.maxWidth};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.xxl};
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: ${props => props.theme.spacing.xxl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: ${props => props.theme.spacing.lg};
    padding: 0 ${props => props.theme.spacing.md};
  }
`;

const HeroVisuals = styled.div`
  position: relative;
  height: 600px;
  width: 100%;
  perspective: 800px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: 350px;
    margin-top: ${props => props.theme.spacing.lg};
  }
`;

// Visual Stack Components
const StackCard = styled.div`
  position: absolute;
  width: 100%;
  max-width: 480px; /* Wider for landscape */
  aspect-ratio: 16/10; /* Landscape Report Slide */
  background: white;
  border-radius: ${props => props.theme.radii.lg};
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  border: 1px solid rgba(0,0,0,0.08);
  overflow: hidden;
  transition: all 0.4s ease;
  transform-origin: center bottom;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* Specific Card Positions - Fanned vertically for landscape */
  &:nth-child(1) {
    z-index: 1;
    transform: scale(0.9) translateY(-200px) translateX(150px);
    opacity: 0.9;
    filter: blur(0.5px);
  }
  &:nth-child(2) {
    z-index: 2;
    transform: scale(0.95) translateY(-100px) translateX(75px);
    opacity: 0.9;
  }
  &:nth-child(3) {
    z-index: 3;
    transform: scale(1) translateY(0) translateX(0);
    box-shadow: 0 25px 50px rgba(0,0,0,0.2);
  }

  /* Hover effect: Expand stack vertically */
  ${HeroVisuals}:hover & {
    &:nth-child(1) { transform: scale(0.9) translateY(-90px); }
    &:nth-child(2) { transform: scale(0.95) translateY(-50px); }
    &:nth-child(3) { transform: scale(1.02) translateY(0); }
  }
`;

// Security Badges
const TrustBadges = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.lg};
  align-items: center;
  opacity: 0.8;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }
`;

const BadgeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textLight};
  font-weight: 500;

  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.theme.colors.sage}; // Use sage green for trust
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xxl};
  max-width: ${props => props.theme.spacing.maxWidth};
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xxl};
  align-items: start;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const PreviewColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const FormColumn = styled.div`
  position: sticky;
  top: 120px;
  z-index: 10;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      position: static;
  }
`;

const SmallHeading = styled(Heading)`
    font-size: 1.25rem;
    color: ${props => props.theme.colors.terracotta};
    margin-bottom: ${props => props.theme.spacing.md};
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: bold;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

const FeatureItem = styled.li`
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: 1rem;
  display: flex;
  gap: 16px;
  align-items: start;
  
  strong {
      display: block;
      margin-bottom: 4px;
      font-size: 1.1rem;
      color: ${props => props.theme.colors.charcoal};
  }
  
  p {
      color: ${props => props.theme.colors.text};
      line-height: 1.5;
  }
`;

const IconWrapper = styled.div`
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    color: ${props => props.theme.colors.terracotta};
    margin-top: 2px;
`;

const WhoIsThisForCard = styled.div`
  background: white;
  padding: 2.5rem;
  border-radius: ${props => props.theme.radii.lg};
  border-top: 4px solid ${props => props.theme.colors.terracotta};
  box-shadow: ${props => props.theme.shadows.md};
  position: relative;
  overflow: hidden;

  &::before {
      content: 'TARGET AUDIENCE';
      position: absolute;
      top: 1rem;
      right: 1.5rem;
      font-size: 0.7rem;
      letter-spacing: 2px;
      font-weight: bold;
      color: ${props => props.theme.colors.textLight};
      opacity: 0.5;
  }
`;

const AccordionItem = styled.div`
    border-bottom: 1px solid ${props => props.theme.colors.border};
    padding: ${props => props.theme.spacing.md} 0;
`;

const AccordionTitle = styled.h4`
    font-size: 1.1rem;
    cursor: pointer;
    margin: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: ${props => props.theme.colors.charcoal};
    padding: 0.5rem 0;
    transition: color 0.2s;
    
    &:hover {
        color: ${props => props.theme.colors.terracotta};
    }
`;

const AccordionIcon = styled.span`
    font-size: 1.5rem;
    line-height: 1;
    font-weight: 300;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${props => props.$isOpen ? 'rotate(45deg)' : 'rotate(0)'};
`;

const AccordionContent = styled.div`
    color: ${props => props.theme.colors.text};
    line-height: 1.6;
    overflow: hidden;
    max-height: ${props => props.$isOpen ? '300px' : '0'};
    opacity: ${props => props.$isOpen ? '1' : '0'};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    margin-top: ${props => props.$isOpen ? props.theme.spacing.sm : '0'};
`;

// Sticky Mobile CTA
const MobileStickyBar = styled.div`
  display: none;
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: ${props => props.$visible ? 'flex' : 'none'};
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 1rem;
    background: white;
    box-shadow: 0 -4px 10px rgba(0,0,0,0.1);
    z-index: 100;
    justify-content: center;
    align-items: center;
    animation: ${fadeIn} 0.3s ease-out;
  }
`;

// --- Feature Showcase Section ---
const ShowcaseSection = styled.section`
  padding: ${props => props.theme.spacing.xxl} 0;
  background: ${props => props.theme.colors.bone};
  position: relative;
  z-index: 5;
`;

const ShowcaseContainer = styled.div`
  max-width: ${props => props.theme.spacing.maxWidth};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.xxl};
`;

const ShowcaseGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: ${props => props.theme.spacing.xxl};
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const TabList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const TabItem = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-left: 4px solid ${props => props.$active ? props.theme.colors.terracotta : 'transparent'};
  background: ${props => props.$active ? 'white' : 'transparent'};
  border-radius: ${props => props.$active ? `0 ${props.theme.radii.md} ${props.theme.radii.md} 0` : '0'};
  box-shadow: ${props => props.$active ? props.theme.shadows.md : 'none'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.$active ? 'white' : 'rgba(255,255,255,0.5)'};
  }

  h4 {
    color: ${props => props.$active ? props.theme.colors.charcoal : props.theme.colors.textLight};
    margin: 0 0 0.5rem 0;
    font-size: 1.2rem;
    font-weight: bold;
    transition: color 0.3s ease;
  }

  p {
    margin: 0;
    color: ${props => props.theme.colors.text};
    font-size: 0.95rem;
    line-height: 1.5;
    display: ${props => props.$active ? 'block' : 'none'};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    p { display: block; opacity: ${props => props.$active ? 1 : 0.7}; }
  }
`;

const FeaturePreview = styled.div`
  position: relative;
  border-radius: ${props => props.theme.radii.lg};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.xl};
  aspect-ratio: 16/10;
  background: ${props => props.theme.colors.charcoal};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.4s ease;
  }
`;

const AssessmentLandingPage = () => {
  const theme = useTheme();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);


  const features = [
    {
      title: "1. Investment Case Statement",
      desc: "Not what you think you're arguing—what the slides actually say. With a confidence score so you know if it's clear or muddled.",
      img: "/assets/assessment-report-1.png"
    },
    {
      title: "2. Strategic Analysis",
      desc: "Where your deck is strong and where it undermines your investibility: go-to-market, unit economics, competitive moat, team, and more.",
      img: "/assets/assessment-report-2.png"
    },
    {
      title: "3. Investor Questions",
      desc: "The critical gaps they'll notice in the first meeting. High-priority concerns that'll come up in diligence vs stuff you can defer.",
      img: "/assets/assessment-report-3.png"
    }
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  useEffect(() => {
    const handleScroll = () => {
      const formElement = document.getElementById('assessment-form');
      if (formElement) {
        const rect = formElement.getBoundingClientRect();
        // Show sticky CTA if bottom of form is above viewport (scrolled past)
        if (rect.bottom < 0) {
          setShowStickyCTA(true);
        } else {
          setShowStickyCTA(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToForm = () => {
    document.getElementById('assessment-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <PageWrapper>
      <Header isDark={true} />

      {/* Dark Theme Hero */}
      <HeroSection style={{ backgroundImage: "url('/assets/patterns/Contours - clipped (dark).svg')" }}>
        <HeroContent>
          <div>
            <Heading as="h1" size="h2" $mb="1rem" color={theme.colors.bone}>
              Does your deck say what you think??<br />
              <span style={{ color: theme.colors.sage }}>Find out here.</span>
            </Heading>
            <Text size="large" color={theme.colors.bone} $mb="2rem" style={{ opacity: 0.9 }}>
              Avoid the polite passes from investors who don't see what you do. Get a detailed analysis of what investment case your deck is making in 60 seconds.
            </Text>

            <Button onClick={scrollToForm} size="large" $variant="primary">Get your Strategy Audit (Beta)</Button>

            <TrustBadges>
              <BadgeItem style={{ color: '#bcbcbc' }}>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: theme.colors.sage }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                Encrypted & Confidential
              </BadgeItem>
              <BadgeItem style={{ color: '#bcbcbc' }}>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: theme.colors.sage }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                No 3rd Party Sharing
              </BadgeItem>
            </TrustBadges>
          </div>


          <HeroVisuals>
            {/* Card 3: Questions - Bottom Card */}
            <StackCard>
              <img src="/assets/assessment-report-3.png" alt="Priority Investor Questions" />
            </StackCard>

            {/* Card 2: Strategy Gaps - Middle Card */}
            <StackCard>
              <img src="/assets/assessment-report-2.png" alt="Strategic Gap Map" />
            </StackCard>

            {/* Card 1: Investment Case - Top Card */}
            <StackCard>
              <img src="/assets/assessment-report-1.png" alt="Investment Case Score" />
            </StackCard>
          </HeroVisuals>
        </HeroContent>
      </HeroSection>

      <ContentGrid>
        <PreviewColumn>
          {/* WHY CHECK */}
          <div>
            <SmallHeading as="h3">Why Check?</SmallHeading>
            <FeatureList>
              <FeatureItem>
                <IconWrapper>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                </IconWrapper>
                <div>
                  <strong>VCs are increasingly using AI as a first filter</strong>
                  <p>Understand what the machines see before you get filtered out for a fixable positioning issue.</p>
                </div>
              </FeatureItem>
              <FeatureItem>
                <IconWrapper>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                </IconWrapper>
                <div>
                  <strong>It's hard to narrate your story when you're the protagonist</strong>
                  <p>Get a fresh pair of eyes (AI, but prompted by a deck expert) to show you what your deck is actually saying.</p>
                </div>
              </FeatureItem>
              <FeatureItem>
                <IconWrapper>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                </IconWrapper>
                <div>
                  <strong>Prepare for investor questions—or defuse them in advance</strong>
                  <p>See what concerns your deck raises so you can address them before they become objections.</p>
                </div>
              </FeatureItem>
            </FeatureList>
          </div>

          {/* WHO THIS IS FOR - QUALIFICATION CARD */}
          <WhoIsThisForCard>
            <SmallHeading as="h3">Who This Is For</SmallHeading>
            <Text><strong>Hard tech founders.</strong> You're building in atoms, not bits (robotics, materials, biotech, climate, defence, space etc).</Text>
            <Text>You know your deck isn't landing but you're too close to see why.</Text>
            <Text>If you've been:</Text>
            <ul style={{ paddingLeft: '20px', marginBottom: '1rem' }}>
              <li>Rewriting slides at midnight</li>
              <li>Getting confused feedback from advisors</li>
              <li>Suspecting something's off but can't pinpoint what</li>
            </ul>
            <Text style={{ fontWeight: 'bold' }}>...then upload your deck and find out.</Text>
          </WhoIsThisForCard>
        </PreviewColumn>

        <FormColumn id="assessment-form">
          <AssessmentForm />
        </FormColumn>
      </ContentGrid>

      {/* WHAT YOU GET - DEEP DIVE SECTION */}
      <ShowcaseSection>
        <ShowcaseContainer>
          <Heading as="h2" size="h2" color="charcoal" $mb="3rem" style={{ textAlign: 'center' }}>What You Get In Your Report</Heading>

          <ShowcaseGrid>
            <TabList>
              {features.map((f, i) => (
                <TabItem
                  key={i}
                  $active={activeFeature === i}
                  onClick={() => setActiveFeature(i)}
                >
                  <h4>{f.title}</h4>
                  <p>{f.desc}</p>
                </TabItem>
              ))}
            </TabList>

            <FeaturePreview>
              <img src={features[activeFeature].img} alt={features[activeFeature].title} />
            </FeaturePreview>
          </ShowcaseGrid>
        </ShowcaseContainer>
      </ShowcaseSection>

      {/* FAQ Styled as 2-Col Grid */}
      <div style={{ background: '#fff', padding: '5rem 0' }}>
        <div style={{ maxWidth: theme.spacing.maxWidth, margin: '0 auto', padding: `0 ${theme.spacing.xxl}` }}>
          <Heading as="h3" size="h2" color="charcoal" $mb="3rem" style={{ textAlign: 'center' }}>Frequency Asked Questions</Heading>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            <div>
              {[
                { q: "Does this check for typos and formatting?", a: "No. This analyzes your strategy: whether your investment case is coherent, where the gaps are, what questions investors will ask." },
                { q: "What if my deck is early-stage / pre-product?", a: "If you don't have slides yet, this won't help you. If you have a deck but it's rough, the tool will still identify what investment case you're presenting and where the strategic logic breaks down." },
                { q: "Do I need to fix everything the assessment flags?", a: "No. Some gaps are critical (investors will reject you for them). Some are medium (they'll ask questions, you can handle them in conversation). Some are minor (nice to have, not deal-breakers)." }
              ].map((item, index) => (
                <AccordionItem key={index}>
                  <AccordionTitle onClick={() => toggleFAQ(index)}>
                    {item.q}
                    <AccordionIcon $isOpen={openFAQ === index}>+</AccordionIcon>
                  </AccordionTitle>
                  <AccordionContent $isOpen={openFAQ === index}>
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </div>
            <div>
              {[
                { q: "What happens to my deck after I upload?", a: "It gets analyzed and then stored in a SOC2 compliant database. Metadata from the deck might be used in wider analysis but it will be anonymised and aggregated. Your slides and company information remains confidential." },
                { q: "Is this a lead magnet to sell me something?", a: "Yes, it is. If your deck has major strategic gaps, you'll probably need help fixing them and I'd love to be the one who helps you. But the assessment itself is free and designed to be useful regardless of whether you decide to work with me." }
              ].map((item, index) => (
                <AccordionItem key={index + 3}>
                  <AccordionTitle onClick={() => toggleFAQ(index + 3)}>
                    {item.q}
                    <AccordionIcon $isOpen={openFAQ === (index + 3)}>+</AccordionIcon>
                  </AccordionTitle>
                  <AccordionContent $isOpen={openFAQ === (index + 3)}>
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </div>
          </div>
        </div>
      </div>

      <MobileStickyBar $visible={showStickyCTA}>
        <Button onClick={scrollToForm} style={{ width: '100%' }}>Assess My Deck</Button>
      </MobileStickyBar>

    </PageWrapper>
  );
};

export default AssessmentLandingPage;
